import React, { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Dialog } from 'components/ui'
import { Button } from 'components/ui'
import CloseButton from 'components/ui/CloseButton'
import classNames from 'classnames'

/**
 * BaseModal - Componente genérico de modal con header, contenido scrolleable y footer fijos
 *
 * Características:
 * - Header fijo (no scroll)
 * - Contenido con scroll
 * - Footer fijo (no scroll)
 * - Responsive: 80% altura en desktop, pantalla completa en mobile
 * - Bloquea scroll del body cuando está abierto
 * - Margin auto en los costados
 */
const BaseModal = ({
    isOpen,
    onClose,
    title,
    headerExtra,
    children,
    footer,
    width = 700,
    closable = true,
    className,
    headerClassName,
    contentClassName,
    contentStyle,
    footerClassName,
    ...rest
}) => {
    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    const resolveResponsiveWidth = useCallback((value) => {
        if (value === undefined || value === null) {
            return value
        }

        const viewportFallback = 'calc(100vw - 3rem)'

        if (typeof value === 'number') {
            return `min(${value}px, ${viewportFallback})`
        }

        if (typeof value === 'string') {
            const matchPx = value.match(/^(\d+(?:\.\d+)?)px$/)
            if (matchPx) {
                const numeric = Number(matchPx[1])
                if (!Number.isNaN(numeric)) {
                    return `min(${numeric}px, ${viewportFallback})`
                }
            }
        }

        return value
    }, [])

    const hasHeaderContent = Boolean(title || headerExtra)
    const useHeaderCloseButton = hasHeaderContent && closable

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            width={resolveResponsiveWidth(width)}
            closable={closable && !useHeaderCloseButton}
            className={classNames(
                'base-modal',
                className
            )}
            contentClassName="h-full flex flex-col base-modal-dialog-content"
            {...rest}
        >
            {/* Header - Fijo */}
            {hasHeaderContent && (
                <div className={classNames(
                    'base-modal-header',
                    'px-6 py-4 border-b border-gray-200 dark:border-gray-600',
                    'flex-shrink-0 relative',
                    'flex items-center gap-4',
                    headerClassName
                )}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex-1">
                        {title}
                    </h3>
                    {(headerExtra || useHeaderCloseButton) && (
                        <div className="flex items-center gap-3">
                            {headerExtra}
                            {useHeaderCloseButton && (
                                <CloseButton onClick={onClose} />
                            )}
                        </div>
                    )}
                </div>
            )}


            {/* Content - Scrolleable */}
            <div className={classNames(
                'base-modal-content',
                'flex-1 min-h-0 overflow-y-auto px-8 py-3',
                contentClassName
            )} style={contentStyle}>
                {children}
            </div>

            {/* Footer - Fijo */}
            {footer && (
                <div className={classNames(
                    'base-modal-footer',
                    'px-6 py-4 border-t border-gray-200 dark:border-gray-600',
                    'flex-shrink-0',
                    footerClassName
                )}>
                    {footer}
                </div>
            )}
        </Dialog>
    )
}

BaseModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node,
    headerExtra: PropTypes.node,
    children: PropTypes.node,
    footer: PropTypes.node,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    closable: PropTypes.bool,
    className: PropTypes.string,
    headerClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    contentStyle: PropTypes.object,
    footerClassName: PropTypes.string,
}

export default BaseModal
